import Link from 'next/link';
import { useRouter } from 'next/router';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { type DropTargetMonitor, useDrop } from 'react-dnd';
import { useSelector } from 'react-redux';

import { useTrackSidebarInteraction } from '@air/analytics';
import { TreeItemAction } from '@air/component-tree';
import { Ellipsis, Plus, TriangleDown, Web } from '@air/next-icons';
import { DropdownMenu, type DropdownMenuProps, renderDropdownItems } from '@air/primitive-dropdown-menu';
import { Tooltip } from '@air/primitive-tooltip';
import { VisuallyHidden } from '@air/primitive-visually-hidden';
import { useAirModal } from '@air/provider-modal';

import { useGeneralLibraryMenuOptions } from '~/components/LibraryBeta/GeneralLibraryPage/hooks/useGeneralLibraryMenuOptions';
import { useGeneralLibraryMetadata } from '~/components/LibraryBeta/hooks/useGeneralLibraryMetadata';
import { useGeneralLibraryTitle } from '~/components/LibraryBeta/hooks/useGeneralLibraryTitle';
import { CreateBoardModal } from '~/components/Modals/CreateBoardModal';
import { boardDragTypes, DragType, type DropData } from '~/components/Shared/Drag/dragTypes';
import { WorkspaceSidebarBoardsList } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarBoardsList';
import { useWorkspaceSidebarSectionStore } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarSectionStoreContext';
import { useWorkspaceSidebarPushLock } from '~/components/v2/hooks/useWorkspaceSidebarPushLock';
import { getCanvasQueryParams } from '~/constants/canvasQueryParams';
import { Pathname } from '~/constants/pathname';
import { Routes } from '~/constants/routes';
import { useFetchObjectsPermissions } from '~/hooks/useFetchObjectsPermissions';
import { useWorkspaceBoardRearrange } from '~/hooks/useWorkspaceBoardRearrange';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import {
  centralizedBoardLibrarySelector,
  isCentralizedBoardInGeneralLibrarySelector,
} from '~/store/centralizedBoard/selectors';
import { useWorkspaceBoards } from '~/swr-hooks/boards/useWorkspaceBoards';
import { GeneralLibrary } from '~/utils/librariesUtils';
import { canCreateBoard } from '~/utils/permissions/boardPermissions';

export const WorkspaceSidebarLibrariesListItemGeneral = memo(() => {
  const router = useRouter();
  const isCentralizedBoardInGeneralLibrary = useSelector(isCentralizedBoardInGeneralLibrarySelector);
  const centralizedBoardLibraryId = useSelector(centralizedBoardLibrarySelector);
  const { currentWorkspace } = useCurrentWorkspace();
  const { generalLibraryTitle } = useGeneralLibraryTitle();
  const { generalLibraryIcon, generalLibraryColor } = useGeneralLibraryMetadata();
  const open = useWorkspaceSidebarSectionStore((store) => !!store.getTreeItem({ id: GeneralLibrary.id })?.open);
  const touched = useWorkspaceSidebarSectionStore((store) => !!store.getTreeItem({ id: GeneralLibrary.id })?.touched);
  const setTreeItem = useWorkspaceSidebarSectionStore((state) => state.setTreeItem);
  const toggleTreeItemOpen = useWorkspaceSidebarSectionStore((state) => state.toggleTreeItemOpen);
  const { data: workspaceBoards, isInitialLoading: isLoadingBoards } = useWorkspaceBoards({
    workspaceId: currentWorkspace?.id,
    view: 'root',
    includeDirectDescendantBoardCount: true,
    includeDirectDescendantCount: true,
  });
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { menuOptions } = useGeneralLibraryMenuOptions();
  const { data: permissions } = useCurrentWorkspacePermissionsContext();
  const [showCreateBoardModal] = useAirModal(CreateBoardModal);
  const { trackSidebarInteraction } = useTrackSidebarInteraction();
  const { onBoardRearrange, getCanRearrange } = useWorkspaceBoardRearrange({ workspacePermissions: permissions });

  const canCreateGeneralLibraryBoard = canCreateBoard(permissions);

  const isActiveRoute = useMemo(() => router.pathname === Pathname.LibraryGeneral, [router.pathname]);
  const isBoardRoute = useMemo(() => router.pathname === Pathname.BoardByBoardId, [router.pathname]);

  useFetchObjectsPermissions({
    objects: {
      boardIds: workspaceBoards?.map((board) => board.id),
    },
  });

  useEffect(() => {
    /**
     * Early return if `centralizedBoardLibraryId` is defined.
     * This means the user is currently viewing a centralized board library,
     * not the general library. In this case, no sidebar state update is needed.
     * Only proceed if `centralizedBoardLibraryId` is undefined, which indicates
     * the user is in the general library context.
     */
    if (centralizedBoardLibraryId !== undefined) {
      return;
    }

    if (touched) {
      return;
    }

    if (isCentralizedBoardInGeneralLibrary && isBoardRoute) {
      setTreeItem({ id: GeneralLibrary.id, open: true, touched: false });
    }
  }, [centralizedBoardLibraryId, isCentralizedBoardInGeneralLibrary, setTreeItem, touched, isBoardRoute]);

  const onToggleOpen = useCallback(() => {
    toggleTreeItemOpen({ id: GeneralLibrary.id });

    trackSidebarInteraction({
      action: open ? 'collapse' : 'expand',
      type: 'library',
    });
  }, [toggleTreeItemOpen, open, trackSidebarInteraction]);

  const onCreateBoard = useCallback(() => {
    showCreateBoardModal({
      trackLocation: 'library',
    });

    trackSidebarInteraction({
      action: 'create_board',
      type: 'library',
    });
  }, [showCreateBoardModal, trackSidebarInteraction]);

  const { handleOpenChange: handleLockOpenChange } = useWorkspaceSidebarPushLock({
    token: 'sidebar-general-library',
  });

  const onDropdownOpenChange = useCallback<Required<DropdownMenuProps>['onOpenChange']>(
    (open) => {
      setIsMenuOpen(open);
      handleLockOpenChange(open);

      if (open) {
        trackSidebarInteraction({
          action: 'open_sub_menu',
          type: 'library',
        });
      }
    },
    [handleLockOpenChange, trackSidebarInteraction],
  );

  const checkIfCanDrop = useCallback(
    (monitor: DropTargetMonitor) => {
      const item = monitor.getItem();

      if (!item) {
        return false;
      }

      if (!canCreateBoard(permissions)) {
        return false;
      }

      // Allow dropping boards that aren't already in workspace root (same logic as WorkspaceSidebarWorkspaceBoards)
      if (item?.firstItem?.library?.id || item?.firstItem?.ancestors?.length !== 0) {
        return true;
      }

      return false;
    },
    [permissions],
  );

  const [{ canDrop, isOver }, drop] = useDrop({
    accept: boardDragTypes,
    drop: (): DropData => ({
      type: 'move',
      location: 'sub-nav-rearrange',
    }),
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: checkIfCanDrop(monitor),
    }),
    canDrop: (_props, monitor) => checkIfCanDrop(monitor),
  });

  const onLibraryClick = useCallback(() => {
    if (touched || open) {
      trackSidebarInteraction({
        action: 'link_click',
        type: 'library',
      });
    } else {
      setTreeItem({ id: GeneralLibrary.id, open: true, touched: false });

      trackSidebarInteraction({
        action: 'link_click_and_expand',
        type: 'library',
      });
    }
  }, [touched, open, setTreeItem, trackSidebarInteraction]);

  return (
    <div className="flex flex-col gap-1">
      <div
        ref={drop}
        className="group relative flex h-10 shrink-0 cursor-pointer items-center gap-3 rounded-8 p-2 font-normal text-14 text-grey-11 outline-none transition-colors hover:bg-grey-5 focus-visible:ring-2 focus-visible:ring-blue-9 focus-visible:ring-inset active:bg-grey-5 data-[dnd-over=true]:data-[dnd-droppable=true]:z-10 data-[dnd-over=true]:data-[dnd-droppable=true]:bg-grey-3 data-[dnd-over=true]:data-[dnd-droppable=true]:ring-2 data-[dnd-over=true]:data-[dnd-droppable=true]:ring-blue-9 data-[menu-open=true]:bg-grey-5"
        data-active={isActiveRoute}
        data-dnd-over={isOver}
        data-dnd-droppable={canDrop}
        data-menu-open={isMenuOpen}
        data-open={open}
      >
        <div
          className="relative flex size-6 shrink-0 cursor-pointer items-center justify-center rounded-4 bg-grey-4 text-14 ring-1 ring-grey-4 transition-colors group-hover:ring-transparent"
          style={{ backgroundColor: generalLibraryColor.hex }}
        >
          <span>{generalLibraryIcon}</span>

          <button
            className="absolute inset-0 z-20 flex size-full items-center justify-center rounded-4 bg-grey-5 opacity-0 transition-[background-color,opacity] hover:bg-grey-4 focus-visible:opacity-100 group-hover:opacity-100"
            onClick={onToggleOpen}
            type="button"
          >
            <VisuallyHidden>{open ? 'Collapse' : 'Expand'} library</VisuallyHidden>
            <TriangleDown className="size-4 text-grey-12 transition-transform group-data-[open=false]:-rotate-90 group-data-[open=true]:rotate-0" />
          </button>
        </div>

        <Link
          className="absolute inset-0 z-10 size-full"
          href={{ pathname: Routes.generalLibrary, query: getCanvasQueryParams() }}
          onClick={onLibraryClick}
        >
          <VisuallyHidden>Go to {generalLibraryTitle}</VisuallyHidden>
        </Link>

        {/* Title - with its own navigation button */}
        <div className="flex grow items-center gap-1">
          <span className="truncate">{generalLibraryTitle}</span>
          <div className="relative z-20 shrink-0">
            <Tooltip
              label={`Users in ${currentWorkspace?.name} are automatically members of this library`}
              side="left"
              align="center"
            >
              <Web className="size-4 fill-grey-8" />
            </Tooltip>
          </div>
        </div>

        {/* General Library Actions */}
        <div className="relative z-20 hidden shrink-0 items-center gap-1 transition-opacity group-focus-within:flex group-hover:flex group-data-[menu-open=true]:flex">
          {!!menuOptions.length && (
            <DropdownMenu
              align="start"
              onOpenChange={onDropdownOpenChange}
              open={isMenuOpen}
              trigger={
                <TreeItemAction data-testid="GENERAL_LIBRARY_MENU_BUTTON">
                  <Ellipsis className="size-4" />
                </TreeItemAction>
              }
            >
              {renderDropdownItems({ options: menuOptions })}
            </DropdownMenu>
          )}

          {canCreateGeneralLibraryBoard && (
            <TreeItemAction data-testid="ADD_GENERAL_LIBRARY_BOARD_BUTTON" onClick={onCreateBoard}>
              <Plus className="size-4" />
            </TreeItemAction>
          )}
        </div>
      </div>

      {!isLoadingBoards && open && workspaceBoards?.length > 0 && (
        <WorkspaceSidebarBoardsList
          boards={workspaceBoards}
          depth={2}
          dragType={DragType.boardNavWorkspace}
          draggable={true}
          droppable={true}
          getCanRearrange={getCanRearrange}
          onBoardRearrange={onBoardRearrange}
          parentBoardId={null}
        />
      )}
    </div>
  );
});

WorkspaceSidebarLibrariesListItemGeneral.displayName = 'GeneralLibraryItem';
